import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Grid,
  Button,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import AdminDashboardCard from "../../components/Custom/AdminDashboardCard";
import CustomAppBar from "../../components/AppBar";
import { useNavigate } from "react-router-dom";
import {
  getFranchiseFarmers,
  getFranchiseStats,
  getMyReferralCoupon,
} from "../../api/franchise";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { CopyAllOutlined, CurrencyRupee } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: theme.spacing(-4),
  },
  container: {
    padding: theme.spacing(3),
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: theme.spacing(2),
    borderRadius: "4px",
    marginBottom: theme.spacing(2.5),
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  statsContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    flexWrap: "wrap",
  },
}));

export default function FranchiseDashboard() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState({});
  const [farmers, setFarmers] = useState([]);
  const [couponData, setCouponData] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });
  const [isLoading] = useState(false);
  const [error] = useState("");

  // Stats items
  const uniformColor = "#4CAF50";

  const statsItems = [
    {
      label: t("Total Farmer Payments"),
      value: "₹" + stats.total_farmer_payments || 0,
      color: uniformColor,
    },
    {
      label: t("Total Farmers"),
      value: stats.farmer_count || 0,
      color: uniformColor,
    },
    {
      label: t("Total Farms"),
      value: stats.total_farms || 0,
      color: uniformColor,
    },
    {
      label: t("Premium Farms"),
      value: stats.premium_farm_count || 0,
      color: uniformColor,
    },
    {
      label: t("Basic Farms"),
      value: stats.basic_farm_count || 0,
      color: uniformColor,
    },
    {
      label: t("Area Mapped (in acres)"),
      value: stats.total_farm_area || 0,
      color: uniformColor,
    },
    {
      label: t("Total Payouts"),
      value: stats.total_income || 0,
      color: uniformColor,
    },
    {
      label: t("Soil Tests Done"),
      value: stats.soil_tests_done || 0,
      color: uniformColor,
    },
  ];

  // Define table columns
  const columns = [
    { key: "full_name", label: t("Farmer Name") },
    { key: "phone_number", label: t("Phone") },
    { key: "date_joined", label: t("Date Joined") },
    { key: "total_order_value", label: t("Revenue") + "(₹)" },
    { key: "area", label: t("Area Mapped (acres)") },
    { key: "premium_subscription_count", label: t("Premium Farms") },
    { key: "basic_subscription_count", label: t("Basic Farms") },
    // { key: "soil_tests", label: t("Soil Tests") },
  ];

  const fetchStats = async () => {
    const stats = await getFranchiseStats();
    if (stats) setStats(stats);
  };

  const fetchFarmersData = async () => {
    const data = await getFranchiseFarmers();
    if (data) setFarmers(data);
  };

  const fetchMyReferralCoupon = async () => {
    const data = await getMyReferralCoupon();
    setCouponData(data);
  };

  useEffect(() => {
    fetchStats();
    fetchFarmersData();
    fetchMyReferralCoupon();
  }, []);

  const handleCopyCoupon = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard");
  };

  const handleShareOnWhatsApp = () => {
    const inviteLink = `${window.location.origin}/signup?referral_code=${couponData.coupon}`;
    const whatsappMessage = encodeURIComponent(
      `Join FarmApp using my referral code: ${couponData.coupon}\n${inviteLink}`
    );
    window.open(`https://wa.me/?text=${whatsappMessage}`, "_blank");
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
    // Optionally, add logic here to fetch new data based on the page
  };

  return (
    <Box className={classes.root} component={Paper}>
      <CustomAppBar />

      <Box className={classes.container}>
        <Box className={classes.statsContainer}>
          {statsItems.map((stat, index) => (
            <AdminDashboardCard
              key={index}
              label={stat.label}
              value={stat.value}
              color={stat.color}
            />
          ))}
        </Box>

        <Box className={classes.filterContainer}>
          <Typography
            sx={{ cursor: "pointer" }}
            onClick={() => handleCopyCoupon(couponData.coupon)}
          >
            {t("Your Referral Coupon:")} {couponData.coupon}
          </Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={6}>
              <Button
                onClick={() =>
                  handleCopyCoupon(
                    `${window.location.origin}/signup?referral_code=${couponData.coupon}`
                  )
                }
                variant="outlined"
                fullWidth
                startIcon={<CopyAllOutlined />}
              >
                {t("Copy invite link")}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                onClick={handleShareOnWhatsApp}
                variant="outlined"
                fullWidth
                startIcon={<WhatsAppIcon />}
              >
                {t("Share WhatsApp")}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                onClick={() => navigate("/payouts")}
                variant="outlined"
                startIcon={<CurrencyRupee />}
              >
                {t("View Payouts")}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align="center"
                    sx={{
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      fontWeight: "bold",
                      backgroundColor: "primary.main",
                      color: "white",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {t("Loading")}...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {error}
                  </TableCell>
                </TableRow>
              ) : (
                farmers.map((farmer, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align="center"
                        sx={{
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {farmer[column.key] || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={classes.pagination}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            disabled={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}
